// store/slices/checkoutSlice.js
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Contact Info
  contactInfo: {
    name: '',
    phone: '',
    email: '',
    marketingConsent: true
  },
  
  // Address
  selectedAddressId: null,
  selectedAddress: null, // ✅ Full address object

  // Billing Address
  selectedBillingAddressId: null,
  selectedBillingAddress: null,

  // Shipping
  selectedShipping: null,
  shippingRates: null,
  loadingRates: false,
  selectedRate: null,
  
  // Payment
  selectedGatewayId: null,
  selectedGatewayName: null,
  selectedGateway: null,
  
  // Coupon
  appliedCoupon: null,
  
  // UI State
  openSection: 'all',
  sameAsShipping: true,
};

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    // Contact Info Actions
    setContactInfo: (state, action) => {
      state.contactInfo = action.payload;
    },
    updateContactField: (state, action) => {
      const { field, value } = action.payload;
      state.contactInfo[field] = value;
    },
    
    // Address Actions
    setSelectedAddressId: (state, action) => {
      state.selectedAddressId = action.payload;
    },

    // ✅ NEW: Store full address object
    setSelectedAddress: (state, action) => {
      state.selectedAddress = action.payload;
      state.selectedAddressId = action.payload?.id ?? null;
    },

    // Billing Address Actions
    setSelectedBillingAddress: (state, action) => {
      state.selectedBillingAddress = action.payload;
      state.selectedBillingAddressId = action.payload?.id ?? null;
    },
    
    // Shipping Actions
    setSelectedShipping: (state, action) => {
      state.selectedShipping = action.payload;
      // Reset rate when shipping vendor changes
      if (action.payload?.id !== state.selectedShipping?.id) {
        state.selectedRate = null;
      }
    },
    setShippingRates: (state, action) => {
      state.shippingRates = action.payload;
    },
    setLoadingRates: (state, action) => {
      state.loadingRates = action.payload;
    },
    setSelectedRate: (state, action) => {
      state.selectedRate = action.payload;
    },
    
    // Payment Actions
    setSelectedGatewayId: (state, action) => {
      state.selectedGatewayId = action.payload;
    },
    setSelectedGatewayName: (state, action) => {
      state.selectedGatewayName = action.payload;
    },
    setPaymentGateway: (state, action) => {
      if (action.payload === null || (action.payload.id === null && action.payload.name === null)) {
        state.selectedGatewayId = null;
        state.selectedGatewayName = null;
        state.selectedGateway = null;
      } else if (action.payload.gateway) {
        state.selectedGateway = action.payload.gateway;
        state.selectedGatewayId = action.payload.gateway.id;
        state.selectedGatewayName = action.payload.gateway.name;
      } else {
        state.selectedGatewayId = action.payload.id;
        state.selectedGatewayName = action.payload.name;
        state.selectedGateway = null;
      }
    },
    
    // Coupon Actions
    setAppliedCoupon: (state, action) => {
      state.appliedCoupon = action.payload;
    },
    
    // UI Actions
    setOpenSection: (state, action) => {
      state.openSection = action.payload;
    },
    toggleSection: (state, action) => {
      state.openSection = state.openSection === action.payload ? null : action.payload;
    },
    
    // Reset Actions
    resetCheckout: (state) => {
      return initialState;
    },
    
    setSameAsShipping: (state, action) => {
      state.sameAsShipping = action.payload;
    },
    // Initialize from user data
    initializeContactInfo: (state, action) => {
      const { first_name, last_name, phone, email } = action.payload;
      state.contactInfo = {
        name: `${first_name} ${last_name}`,
        phone: phone || '',
        email: email || '',
        marketingConsent: true
      };
    },
  },
});

export const {
  setContactInfo,
  updateContactField,
  setSelectedAddressId,
  setSelectedAddress, // ✅ NEW export
  setSelectedBillingAddress,
  setSelectedShipping,
  setShippingRates,
  setLoadingRates,
  setSelectedRate,
  setSelectedGatewayId,
  setSelectedGatewayName,
  setPaymentGateway,
  setAppliedCoupon,
  setOpenSection,
  toggleSection,
  resetCheckout,
  initializeContactInfo,
  setSameAsShipping,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;

// Selectors
export const selectContactInfo = (state) => state.checkout.contactInfo;
export const selectSelectedAddressId = (state) => state.checkout.selectedAddressId;
export const selectSelectedAddress = (state) => state.checkout.selectedAddress; // ✅ NEW
export const selectSelectedBillingAddressId = (state) => state.checkout.selectedBillingAddressId;
export const selectSelectedBillingAddress = (state) => state.checkout.selectedBillingAddress;
export const selectSelectedShipping = (state) => state.checkout.selectedShipping;
export const selectShippingRates = (state) => state.checkout.shippingRates;
export const selectLoadingRates = (state) => state.checkout.loadingRates;
export const selectSelectedRate = (state) => state.checkout.selectedRate;
export const selectSelectedGatewayId = (state) => state.checkout.selectedGatewayId;
export const selectSelectedGatewayName = (state) => state.checkout.selectedGatewayName;
export const selectSelectedGateway = (state) => state.checkout.selectedGateway;
export const selectAppliedCoupon = (state) => state.checkout.appliedCoupon;
export const selectOpenSection = (state) => state.checkout.openSection;
export const selectSameAsShipping = (state) => state.checkout.sameAsShipping;

// Computed Selectors
export const selectIsContactCompleted = (state) => {
  const { name, phone } = state.checkout.contactInfo;
  return !!(name && phone);
};

export const selectIsShippingCompleted = (state) => {
  return !!state.checkout.selectedRate;  // ← was selectedShipping
};

export const selectIsPaymentCompleted = (state) => {
  return !!state.checkout.selectedGatewayId;
};

export const selectIsCheckoutDisabled = (state) => {
  return !(
    state.checkout.selectedAddressId &&
    (state.checkout.sameAsShipping || state.checkout.selectedBillingAddressId) &&
    state.checkout.selectedRate &&
    state.checkout.selectedGatewayId
  );
};